/**
 * APT349 (the client: "determine the display based on the natural Debit/Credit nature of the Reporting Code ... if the balance is in its normal accounting
 * position, display the amount as a positive value without a negative sign; if it is opposite to its normal accounting position, display the amount in
 * brackets ... a display/presentation change only and must not alter the underlying Debit/Credit accounting value").
 *
 * The API keeps every amount as stored - debits positive, credits negative - and sends each reporting code's natural side with it as `normalSign`
 * (reg.ReportingCode.NormalSign: 1 debit-natured, -1 credit-natured; assets and expenses debit, liabilities, income and capital/equity/reserves credit).
 * Nothing here guesses a side from a code's name. A nominal that has no reporting code yet has no natural side (`normalSign` null/undefined): its stored sign
 * is kept, and, as the client asked ("show in brackets for any negative display"), a negative is still in brackets.
 *
 * Every function is display only: it never changes what is stored or sent back.
 */

/** 1 debit-natured, -1 credit-natured; null/undefined when the amount has no reporting code. */
export type NormalSign = number | null | undefined;

const twoDecimals = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  useGrouping: true,
});

const isCreditNatured = (normalSign: NormalSign) => typeof normalSign === 'number' && normalSign < 0;

/** The amount as a person reads it: a balance on its code's natural side is positive, one on the opposite side negative. */
export function toNatural(stored: number, normalSign: NormalSign): number {
  return isCreditNatured(normalSign) ? -stored : stored;
}

/** The stored amount (debit positive) for an amount typed or read in the natural presentation: the inverse of `toNatural` (the flip is its own inverse). */
export function fromNatural(natural: number, normalSign: NormalSign): number {
  return isCreditNatured(normalSign) ? -natural : natural;
}

/** 1,234.56 / (1,234.56) / 0.00: no sign, brackets for a negative, and a figure that rounds to nil has neither. Empty text for null, undefined or NaN. */
export function formatBracketed(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  const rounded = Math.round((Math.abs(value) + Number.EPSILON) * 100) / 100;
  if (rounded === 0) return twoDecimals.format(0);
  const text = twoDecimals.format(rounded);
  return value < 0 ? `(${text})` : text;
}

/** A stored amount (debit positive, credit negative) of a reporting code, shown in its natural presentation. */
export function formatNatural(stored: number | null | undefined, normalSign: NormalSign): string {
  if (stored === null || stored === undefined || Number.isNaN(stored)) return '';
  return formatBracketed(toNatural(stored, normalSign));
}

/** A percentage in the same presentation, without the % sign (the column says it): 12.50, (12.50) when negative, '-' when there is none. A credit-natured code flips it with its amounts. */
export function formatNaturalPercent(stored: number | null | undefined, normalSign: NormalSign): string {
  if (stored === null || stored === undefined || Number.isNaN(stored)) return '-';
  return formatBracketed(toNatural(stored, normalSign));
}

/**
 * The difference between a schedule and the adjusted trial balance in natural terms - the schedule's natural figure minus the ATB's - so it is positive
 * when the schedule is higher than the trial balance (whichever side the code lies on) and negative, in brackets, when it is lower. Both operands are stored.
 */
export function naturalDifference(scheduleStored: number, atbStored: number, normalSign: NormalSign): number {
  return toNatural(scheduleStored, normalSign) - toNatural(atbStored, normalSign);
}
